import type { MemberSearchOption } from "./memberSearchQuery";
import { jurisdictionContains } from "../../utils/jurisdiction/structuredJurisdiction";

export type MemberSearchOfficeRecord = {
  id?: string;
  name?: string;
  cityName?: string;
  stateName?: string;
  countryName?: string;
  firm?: string;
  /** Structured object or legacy label. */
  jurisdiction?: unknown;
  isSearchable?: boolean;
};

export type MemberSearchFirmRef = {
  id: string;
  /** Structured object, legacy label or legacy GUID. */
  jurisdiction?: unknown;
};

/** Mirrors memberSearch backend: hide the firm's primary jurisdiction office row in firm results. */
export const shouldSkipPrimaryJurisdictionOffice = (
  office: MemberSearchOfficeRecord,
  primaryJurisdictionByFirmId: Map<string, unknown>,
): boolean => {
  const firmId = String(office.firm ?? "").trim();
  return jurisdictionContains(
    primaryJurisdictionByFirmId.get(firmId),
    office.jurisdiction,
  );
};

export const resolveOfficeLocationLabel = (
  office: MemberSearchOfficeRecord,
): string => {
  const name = String(office.name ?? "").trim();
  if (name) {
    return name;
  }

  const cityName = String(office.cityName ?? "").trim();
  const stateName = String(office.stateName ?? "").trim();
  const countryName = String(office.countryName ?? "").trim();

  if (cityName && countryName) {
    return `${cityName}, ${countryName}`;
  }
  if (cityName && stateName) {
    return `${cityName}, ${stateName}`;
  }

  return cityName || stateName || countryName;
};

const isSearchableMemberFirmOffice = (
  office: MemberSearchOfficeRecord,
  memberFirmIds: Set<string>,
): boolean => {
  const id = String(office.id ?? "").trim();
  const label = resolveOfficeLocationLabel(office);
  if (!id || !label || office.isSearchable === false) {
    return false;
  }

  const firmId = String(office.firm ?? "").trim();
  return Boolean(firmId && memberFirmIds.has(firmId));
};

/** Searchable offices for member firms; one row per location label. */
export const buildMemberSearchOfficeLocationOptions = (
  offices: MemberSearchOfficeRecord[],
  memberFirms: MemberSearchFirmRef[],
  filterFirmId?: string,
): MemberSearchOption[] => {
  const memberFirmIds = new Set(memberFirms.map((firm) => firm.id));
  const normalizedFilterFirmId = String(filterFirmId ?? "").trim();
  const byName = new Map<string, { name: string; ids: string[] }>();

  for (const office of offices) {
    if (!isSearchableMemberFirmOffice(office, memberFirmIds)) {
      continue;
    }

    const firmId = String(office.firm ?? "").trim();
    if (normalizedFilterFirmId && firmId !== normalizedFilterFirmId) {
      continue;
    }

    const id = String(office.id ?? "").trim();
    const name = resolveOfficeLocationLabel(office);
    const key = name.toLowerCase();
    const existing = byName.get(key);

    if (existing) {
      if (!existing.ids.includes(id)) {
        existing.ids.push(id);
      }
      continue;
    }

    byName.set(key, { name, ids: [id] });
  }

  return [...byName.values()]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(({ name, ids }) => ({
      id: ids[0],
      name,
      ...(ids.length > 1 ? { relatedOfficeIds: ids } : {}),
    }));
};
